import React, { useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  CreateCompoundProductionStepDto,
  CompoundProductionStepDto,
  CompoundProductionStepType,
} from '@services/dtos'
import { compoundProductionStepSchema } from '@utils/schemas'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { Grid, Typography } from '@mui/material'
import {
  createCompoundProductionStep,
  updateCompoundProductionStep,
} from '@state/reducers/compoundProductionStepReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import SelectField from '../inputs/SelectField'
import { FormCrudHeader } from './FormCrudHeader'
import { Site } from '@/services/api'
import { CompoundProductionStepTypeLabels } from '@utils/constants'
import { humanizeDurationSeconds } from '@utils/date.utils'

const LIBRE_TYPE_OPTIONS = [
  { label: CompoundProductionStepTypeLabels[CompoundProductionStepType.BOOLEAN], value: CompoundProductionStepType.BOOLEAN },
  { label: CompoundProductionStepTypeLabels[CompoundProductionStepType.TEXT], value: CompoundProductionStepType.TEXT },
]

const CATEGORY_OPTIONS = [
  { label: 'Libre', value: 'LIBRE' },
  { label: 'Dilution', value: 'DILUTION' },
  { label: 'Minuteur', value: 'TIMER' },
]

type Category = 'LIBRE' | 'DILUTION' | 'TIMER'

const getInitialCategory = (step?: CompoundProductionStepDto | null): Category => {
  if (step?.type === CompoundProductionStepType.DILUTION) return 'DILUTION'
  if (step?.type === CompoundProductionStepType.TIMER) return 'TIMER'
  return 'LIBRE'
}

type Props = {
  editingCompoundProductionStep?: CompoundProductionStepDto | null
  onClose: () => void
}

const CompoundProductionStepForm: React.FC<Props> = ({
  editingCompoundProductionStep,
  onClose,
}) => {
  const {
    watch,
    handleSubmit,
    reset,
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<CreateCompoundProductionStepDto>({
    defaultValues: editingCompoundProductionStep
      ? {
          code: editingCompoundProductionStep.code,
          siteId: editingCompoundProductionStep.site.id,
          label: editingCompoundProductionStep.label,
          type: editingCompoundProductionStep.type,
          unit: editingCompoundProductionStep.unit,
          expected: editingCompoundProductionStep.expected,
          expectedValue: editingCompoundProductionStep.expectedValue,
          dilutionVolumeMl: editingCompoundProductionStep.dilutionVolumeMl ?? undefined,
          timerDurationSeconds: editingCompoundProductionStep.timerDurationSeconds ?? undefined,
          comment: editingCompoundProductionStep.comment ?? undefined,
        }
      : {},
    resolver: yupResolver(compoundProductionStepSchema),
  })

  const dispatch = useAppDispatch()
  const user = useAppSelector(({ auth }) => auth.user)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [category, setCategory] = useState<Category>(getInitialCategory(editingCompoundProductionStep))

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  const handleCategoryChange = (newCategory: Category) => {
    setCategory(newCategory)
    if (newCategory === 'DILUTION') {
      setValue('type', CompoundProductionStepType.DILUTION)
      setValue('unit', undefined)
      setValue('expectedValue', undefined)
      setValue('expected', undefined)
      setValue('timerDurationSeconds', undefined)
      clearErrors(['unit', 'expectedValue', 'expected', 'timerDurationSeconds'])
    } else if (newCategory === 'TIMER') {
      setValue('type', CompoundProductionStepType.TIMER)
      setValue('unit', undefined)
      setValue('expectedValue', undefined)
      setValue('expected', undefined)
      setValue('dilutionVolumeMl', undefined)
      clearErrors(['unit', 'expectedValue', 'expected', 'dilutionVolumeMl'])
    } else {
      setValue('type', CompoundProductionStepType.TEXT)
      setValue('dilutionVolumeMl', undefined)
      setValue('timerDurationSeconds', undefined)
      clearErrors(['dilutionVolumeMl', 'timerDurationSeconds'])
    }
  }

  const type = watch('type')

  useEffect(() => {
    if (type === CompoundProductionStepType.BOOLEAN) {
      setValue('expectedValue', undefined)
      clearErrors('expectedValue')
    } else if (type === CompoundProductionStepType.TEXT) {
      setValue('expected', undefined)
      clearErrors('expected')
    }
  }, [type])

  const onSubmitForm = async (data: CreateCompoundProductionStepDto) => {
    setIsSubmitting(true)
    const displayErrorMessage = () =>
      cb('Une erreur est survenue, vérifiez que le code n\'est pas déjà utilisé')

    if (editingCompoundProductionStep) {
      dispatch(
        updateCompoundProductionStep({
          id: editingCompoundProductionStep.id,
          dto: data,
        }),
      )
        .unwrap()
        .then(() => {
          cb('L\'étape de production a été modifiée avec succès')
          onClose()
        })
        .catch(displayErrorMessage)
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createCompoundProductionStep(data))
        .unwrap()
        .then(() => {
          cb('L\'étape de production a été créée avec succès')
          onClose()
        })
        .catch(displayErrorMessage)
        .finally(() => setIsSubmitting(false))
    }
  }

  const sites = useMemo(() => (user as any & { sites: Site[] })?.sites, [user])

  const timerDurationSeconds = watch('timerDurationSeconds')
  const formattedTimerDurationSeconds = timerDurationSeconds ? humanizeDurationSeconds(timerDurationSeconds) : ''

  return (
    <div className="flex w-full h-full p-4">
      <form onSubmit={handleSubmit(onSubmitForm)} className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingCompoundProductionStep}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Etape de production MRP
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              <Grid item xs={12} sm={6}>
                <InputField
                  control={control}
                  name="code"
                  label="Code"
                  type="text"
                  helperText={errors.code?.message}
                  error={!!errors.code}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputField
                  control={control}
                  name="label"
                  label="Libellé"
                  type="text"
                  helperText={errors.label?.message}
                  error={!!errors.label}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {sites?.length ? (
                  <SelectField
                    control={control}
                    name="siteId"
                    options={sites.map(({ id, label }) => ({
                      label,
                      value: id,
                    }))}
                    value={watch('siteId')}
                    label="Site"
                  />
                ) : (
                  <Typography variant="body1">Aucun site enregistré</Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <SelectField
                  control={control}
                  name={'__category__'}
                  options={CATEGORY_OPTIONS}
                  value={category}
                  onChange={(e) => handleCategoryChange(e.target.value as Category)}
                  label={'Catégorie'}
                />
              </Grid>

              {category === 'LIBRE' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <InputField
                      control={control}
                      name="unit"
                      label="Unité"
                      type="text"
                      helperText={errors.unit?.message}
                      error={!!errors.unit}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SelectField
                      control={control}
                      name={'type'}
                      options={LIBRE_TYPE_OPTIONS}
                      value={watch('type')}
                      label={'Type'}
                    />
                  </Grid>

                  {type === CompoundProductionStepType.TEXT && (
                    <Grid item xs={12}>
                      <InputField
                        control={control}
                        name="expectedValue"
                        label="Valeur attendue"
                        type="text"
                      />
                    </Grid>
                  )}

                  {type === CompoundProductionStepType.BOOLEAN && (
                    <Grid item xs={12}>
                      <SelectField
                        control={control}
                        name={'expected'}
                        onChange={(e) => setValue('expected', (e.target.value as string) === 'true')}
                        options={[
                          { label: 'Oui', value: 'true' },
                          { label: 'Non', value: 'false' },
                        ]}
                        value={watch('expected')}
                        label={'Valeur attendue'}
                      />
                    </Grid>
                  )}
                </>
              )}

              {category === 'DILUTION' && (
                <Grid item xs={12} sm={6}>
                  <InputField
                    control={control}
                    name="dilutionVolumeMl"
                    label="Volume de dilution (mL)"
                    type="number"
                    inputProps={{ step: '0.01', min: 0 }}
                    helperText={errors.dilutionVolumeMl?.message}
                    error={!!errors.dilutionVolumeMl}
                  />
                </Grid>
              )}

              {category === 'TIMER' && (
                <Grid item xs={12} sm={6}>
                  <InputField
                    control={control}
                    name="timerDurationSeconds"
                    label="Durée (secondes)"
                    type="number"
                    inputProps={{ step: '1', min: 1 }}
                    helperText={errors.timerDurationSeconds?.message}
                    error={!!errors.timerDurationSeconds}
                  />
                  {formattedTimerDurationSeconds && (
                    <Typography variant="caption" color="text.secondary">
                      = {formattedTimerDurationSeconds}
                    </Typography>
                  )}
                </Grid>
              )}

              <Grid item xs={12}>
                <InputField
                  control={control}
                  name="comment"
                  label="Commentaire"
                  type="text"
                  multiline
                  rows={2}
                  helperText={errors.comment?.message}
                  error={!!errors.comment}
                />
              </Grid>
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default CompoundProductionStepForm
